<template>
    <div class="card border-0 bg-transparent">
        <app-table
            id="hotel-dashboard-summary-table"
            :options="options"
        />

        <div class="card card-with-shadow border-0 mt-4" v-if="chartHasData">
            <div class="card-body p-primary">
                <app-chart
                    type="dough-chart"
                    :height="450"
                    :labels="chartLabels"
                    :data-sets="chartDataSets"
                    :show-legend="true"
                    :key="chartKey"
                />
            </div>
        </div>
    </div>
</template>

<script>
import HelperMixin from "../../../../../common/Mixin/Global/HelperMixin";
import {TENANT_BASE_URL} from "../../../../../common/Config/UrlHelper";
import {axiosGet} from "../../../../../common/Helper/AxiosHelper";
import {numberFormatter} from "../../../../../common/Helper/Support/SettingsHelper";

export default {
    name: "RoomHousekeepingSummaryTab",
    mixins: [HelperMixin],
    props: {
        hotelId: {
            type: [Number, String],
            default: null,
        },
        maidId: {
            type: [Number, String],
            default: 0,
        },
        employeeTags: {
            type: [Array, String],
            default: '',
        },
        fromDate: {
            type: String,
            default: '',
        },
        toDate: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            chartKey: 0,
            chartLabels: [],
            hasMounted: false,
            totals: {
                total: 0,
                contract_price: 0,
                selling_price: 0,
                profit: 0,
            },
            chartDataSets: [
                {
                    borderWidth: 0,
                    backgroundColor: ['#FF5733', '#088d49'],
                    data: [],
                }
            ],
            options: {
                name: this.$t('summary'),
                url: this.buildTableUrl(),
                showHeader: true,
                showCount: true,
                tableShadow: false,
                tablePaddingClass: 'pt-primary',
                footerRows: [],
                columns: [
                    {
                        title: this.$t('room_type'),
                        type: 'text',
                        key: 'room_type',
                        isVisible: true,
                        sortable: false,
                    },
                    {
                        title: this.$t('cleaning'),
                        type: 'text',
                        key: 'cleaning_type',
                        isVisible: true,
                        sortable: false,
                    },
                    {
                        title: this.$t('total'),
                        type: 'text',
                        key: 'total',
                        isVisible: true,
                        sortable: false,
                    },
                    {
                        title: this.$t('contract_price'),
                        type: 'custom-html',
                        key: 'contract_price',
                        isVisible: true,
                        sortable: false,
                        modifier: value => this.formatAmount(value),
                    },
                    {
                        title: this.$t('selling_price'),
                        type: 'custom-html',
                        key: 'selling_price',
                        isVisible: true,
                        sortable: false,
                        modifier: value => this.formatAmount(value),
                    },
                    {
                        title: this.$t('profit'),
                        type: 'custom-html',
                        key: 'profit',
                        isVisible: true,
                        sortable: false,
                        modifier: value => {
                            const color = Number(value) >= 0 ? 'green' : 'red';

                            return `<span style="color: ${color}">${this.formatAmount(value)}</span>`;
                        },
                    },
                ],
                filters: [],
                paginationType: 'pagination',
                responsive: true,
                rowLimit: 10,
                showAction: false,
                orderBy: 'asc',
            },
        }
    },
    computed: {
        chartHasData() {
            return this.chartDataSets[0].data.some(value => Number(value) > 0);
        },
        requestSignature() {
            return [
                this.hotelId || '',
                this.maidId || 0,
                this.normalizedEmployeeTags,
                this.fromDate || '',
                this.toDate || '',
            ].join('|');
        },
        normalizedEmployeeTags() {
            if (Array.isArray(this.employeeTags)) {
                return this.employeeTags.join(',');
            }
            return this.employeeTags || '';
        }
    },
    watch: {
        requestSignature() {
            if (!this.hasMounted) {
                return;
            }

            this.options.url = this.buildTableUrl();
            this.loadTotals();
            this.loadChartData();
            this.$hub.$emit('reload-hotel-dashboard-summary-table', {
                resetPage: true,
                historyMode: 'replace',
            });
        }
    },
    mounted() {
        this.hasMounted = true;
        this.loadTotals();
        this.loadChartData();
    },
    methods: {
        buildBaseParams() {
            return {
                hotel_id: this.hotelId || '',
                maid_id: this.maidId || 0,
                employee_tags: this.normalizedEmployeeTags,
                from_date: this.fromDate || '',
                to_date: this.toDate || '',
            };
        },
        buildQueryString() {
            return new URLSearchParams(this.buildBaseParams()).toString();
        },
        buildTableUrl() {
            return `${TENANT_BASE_URL}app/hotel/hotel-dashboard/summary-table?${this.buildQueryString()}`;
        },
        buildChartUrl() {
            return `${TENANT_BASE_URL}app/hotel/hotel-dashboard/summary-chart?${this.buildQueryString()}`;
        },
        buildTotalsUrl() {
            return `${TENANT_BASE_URL}app/hotel/hotel-dashboard/summary-totals?${this.buildQueryString()}`;
        },
        clearTotals() {
            this.totals = {
                total: 0,
                contract_price: 0,
                selling_price: 0,
                profit: 0,
            };
            this.options.footerRows = [];
        },
        clearChart() {
            this.chartLabels = [];
            this.chartDataSets[0].data = [];
            this.chartKey += 1;
        },
        loadTotals() {
            if (!this.hotelId) {
                this.clearTotals();
                return;
            }

            axiosGet(this.buildTotalsUrl())
                .then(({data}) => {
                    this.totals = {
                        total: Number(data.total || 0),
                        contract_price: Number(data.contract_price || 0),
                        selling_price: Number(data.selling_price || 0),
                        profit: Number(data.profit || 0),
                    };
                    this.options.footerRows = Number(this.totals.total) > 0 ? [this.buildFooterRow()] : [];
                })
                .catch(error => {
                    this.clearTotals();
                    this.$toastr.e(error.response?.data?.message || error.message);
                });
        },
        buildFooterRow() {
            return {
                rowClassName: 'summary-footer-row font-weight-bold',
                room_type: '',
                cleaning_type: '',
                total: this.totals.total,
                contract_price: this.totals.contract_price,
                selling_price: this.totals.selling_price,
                profit: this.totals.profit,
                footerCells: {
                    room_type: {
                        colspan: 2,
                        className: 'summary-footer-label',
                    },
                    cleaning_type: {
                        hidden: true,
                    },
                },
            };
        },
        loadChartData() {
            if (!this.hotelId) {
                this.clearChart();
                return;
            }

            axiosGet(this.buildChartUrl())
                .then(({data}) => {
                    this.chartLabels = data.labels || [];
                    this.chartDataSets[0].data = data.data || [];
                    this.chartKey += 1;
                })
                .catch(error => {
                    this.clearChart();
                    this.$toastr.e(error.response?.data?.message || error.message);
                });
        },
        formatAmount(value) {
            const settings = window.settings || {};
            const amount = numberFormatter(Number(value || 0));
            const symbol = settings.currency_symbol || '';
            const position = settings.currency_position || 'left';

            return this.$t(`format_${position}`, {symbol, amount});
        },
    }
}
</script>

<style scoped>
::v-deep .summary-footer-row .datatable-td {
    font-size: 1.05rem;
    padding-top: 1.5rem;
    padding-bottom: 1.5rem;
}

::v-deep .summary-footer-row .summary-footer-label {
    font-size: 1.1rem;
}
</style>
